/**
 * Tiny SVG area chart — multi-series, stacked, with x-axis labels.
 */
type Props = {
  data: Array<{ day: string; [platform: string]: number | string }>;
  series: Array<{ key: string; color: string; label: string }>;
  height?: number;
};

export default function Chart({ data, series, height = 220 }: Props) {
  if (!data.length) return <div className="empty">No data yet</div>;
  const width = 720;
  const pad = { l: 40, r: 16, t: 14, b: 24 };

  const totals = data.map((d) =>
    series.reduce((sum, s) => sum + Number(d[s.key] ?? 0), 0)
  );
  const maxY = Math.max(1, ...totals);
  const n = data.length;

  const xOf = (i: number) => pad.l + (i / Math.max(n - 1, 1)) * (width - pad.l - pad.r);
  const yOf = (v: number) => height - pad.b - (v / maxY) * (height - pad.t - pad.b);

  // Build stacked areas.
  const cum = data.map(() => 0);
  const areas = series.map((s) => {
    const topPoints: Array<[number, number]> = [];
    const bottomPoints: Array<[number, number]> = [];
    for (let i = 0; i < n; i++) {
      const v = Number(data[i][s.key] ?? 0);
      const below = cum[i];
      const above = below + v;
      bottomPoints.push([xOf(i), yOf(below)]);
      topPoints.push([xOf(i), yOf(above)]);
      cum[i] = above;
    }
    const path =
      'M' +
      topPoints.map(([x, y]) => `${x},${y}`).join(' L ') +
      ' L ' +
      bottomPoints.reverse().map(([x, y]) => `${x},${y}`).join(' L ') +
      ' Z';
    return { key: s.key, color: s.color, path };
  });

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => maxY * t);

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        {/* grid */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={pad.l}
              x2={width - pad.r}
              y1={yOf(t)}
              y2={yOf(t)}
              stroke="#eef0f2"
              strokeWidth="1"
            />
            <text
              x={pad.l - 6}
              y={yOf(t) + 3}
              fontSize="10"
              textAnchor="end"
              fill="#8c9196"
            >
              ${Math.round(t / 100)}
            </text>
          </g>
        ))}
        {/* areas */}
        {areas.map((a) => (
          <path key={a.key} d={a.path} fill={a.color} fillOpacity="0.6" stroke="none" />
        ))}
        {/* x labels — show every 3rd */}
        {data.map((d, i) =>
          i % 3 === 0 ? (
            <text
              key={i}
              x={xOf(i)}
              y={height - pad.b + 14}
              fontSize="10"
              textAnchor="middle"
              fill="#8c9196"
            >
              {String(d.day).slice(5)}
            </text>
          ) : null
        )}
      </svg>
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.key}>
            <span className="dot" style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
